import { Link } from "react-router-dom";
import { PageShell, LegalSection } from "../components/PageShell";

export default function Terms() {
  return (
    <PageShell eyebrow="Legal" title="Terms of Use">
      <p className="mt-4 text-sm text-neutral-400">Effective date: June 1, 2026 · Last updated: July 14, 2026</p>
      <p className="mt-6 text-[15px] leading-relaxed text-neutral-600">
        These Terms of Use ("Terms") are an agreement between you and Concentration of Risk
        ("we", "us", or "our"), the operator of the Concentration of Risk website and related
        services (the "Service"). They govern your access to and use of the Service. By accessing
        or using the Service, you agree to be bound by these Terms and our{" "}
        <Link to="/privacy" className="text-[#1a1a18] underline underline-offset-2">Privacy Policy</Link>.
        If you do not agree, do not use the Service. PLEASE NOTE: SECTION 17 CONTAINS A BINDING
        ARBITRATION PROVISION AND CLASS-ACTION WAIVER THAT AFFECT YOUR LEGAL RIGHTS.
      </p>

      <LegalSection heading="1. The Service">
        <p>The Service extracts, structures, and displays concentration-of-risk disclosures
        (customer, geographic, supplier, product, and credit) from public filings on SEC EDGAR,
        together with derived presentations such as trend charts, screeners, composite risk levels,
        and alerts. A limited preview is available without an account; a free account provides
        access to the full covered universe. We may change, add, or remove features at any
        time.</p>
      </LegalSection>

      <LegalSection heading="2. Eligibility">
        <p>You must be at least 18 years old and capable of forming a binding contract to use the
        Service. If you use the Service on behalf of an organization, you represent that you are
        authorized to bind that organization to these Terms, and "you" includes the
        organization.</p>
      </LegalSection>

      <LegalSection heading="3. Not investment advice; no fiduciary relationship">
        <p>The Service is a publisher of general, impersonal research derived from public filings.
        Nothing on the Service constitutes investment, financial, legal, tax, or accounting advice,
        an offer or solicitation to buy or sell any security, or a recommendation regarding any
        issuer, strategy, or transaction. Content is not tailored to your circumstances, objectives,
        or risk tolerance, and no content should be understood as a statement about the suitability
        of any investment for you.</p>
        <p>We are not a broker-dealer, investment adviser, or fiduciary; we operate as a publisher
        of general circulation, and no advisory, fiduciary, or professional relationship is created
        by your use of the Service. Composite levels ("High", "Medium", "Low") and similar derived
        indicators reflect our editorial methodology applied to disclosed figures — they are not
        credit ratings, price targets, or predictions of any outcome. A high or low concentration
        reading is not a signal that any security will rise or fall.</p>
        <p>YOU ARE SOLELY RESPONSIBLE FOR YOUR INVESTMENT DECISIONS. BEFORE ACTING ON ANY
        INFORMATION FOUND ON THE SERVICE, VERIFY IT AGAINST THE ORIGINAL SEC FILING AND CONSULT A
        QUALIFIED PROFESSIONAL. WE ACCEPT NO LIABILITY FOR TRADING OR INVESTMENT LOSSES.</p>
      </LegalSection>

      <LegalSection heading="4. Data reliability — no guarantee of accuracy">
        <p><strong className="text-neutral-800">How the data is produced.</strong> Figures are
        extracted programmatically from SEC filings using a combination of structured (XBRL)
        parsing and AI-assisted analysis of filing text, and displayed figures are generally
        linked back to their source documents where a source location can be identified. We run
        automated verification checks against the original filings, but not every figure can be
        matched or verified, and automated and AI-assisted extraction is inherently imperfect.</p>
        <p><strong className="text-neutral-800">Limitations of the data.</strong> Extraction,
        classification, and interpretation errors can occur and survive our checks; filings can be
        amended or restated after we process them; companies measure disclosures against different
        bases (total revenue, receivables, a segment or line of business) and change those bases
        between periods; some disclosures state only a minimum threshold (shown as "≥X%"), not an
        exact figure; coverage may be incomplete; and there can be a delay between a filing
        appearing on EDGAR and appearing on the Service.</p>
        <p>WE DO NOT WARRANT OR GUARANTEE THE ACCURACY, COMPLETENESS, TIMELINESS, OR RELIABILITY OF
        ANY DATA ON THE SERVICE. ALL DATA IS PROVIDED "AS IS" FOR CONVENIENCE ONLY. THE ORIGINAL
        FILING AS PUBLISHED ON SEC EDGAR IS THE SOLE AUTHORITATIVE SOURCE, AND YOU AGREE TO VERIFY
        ANY FIGURE AGAINST THE ORIGINAL SEC SOURCE BEFORE RELYING ON IT. YOU ASSUME ALL RISK
        ARISING FROM YOUR USE OF, OR RELIANCE ON, ANY DATA ON THE SERVICE.</p>
        <p>Links to source documents are provided for convenience only; we do not warrant that any
        link points to the correct, current, or complete location of the underlying disclosure.
        SEC EDGAR content belongs to its respective filers; we are not affiliated with or endorsed
        by the U.S. Securities and Exchange Commission or any issuer shown on the Service.
        Suspected errors may be reported via the{" "}
        <Link to="/contact" className="text-[#1a1a18] underline underline-offset-2">contact page</Link>;
        we may, but are not obligated to, review or correct any reported item.</p>
      </LegalSection>

      <LegalSection heading="5. Accounts">
        <p>You may sign in with Google or with a single-use email link. You must provide accurate
        information, keep control of your email account and sign-in method, and promptly notify us
        of any unauthorized use. You are responsible for activity under your account. Use of Google
        sign-in is additionally subject to Google's own terms. We may suspend or terminate accounts
        that violate these Terms. One person per account; accounts may not be shared or
        transferred.</p>
      </LegalSection>

      <LegalSection heading="6. Free and paid plans">
        <p>The current free tier may be changed, limited, or withdrawn at any time. If and when we
        offer paid subscriptions: (a) payments are processed by Stripe under its own terms;
        (b) subscriptions renew automatically at the then-current price until cancelled;
        (c) you may cancel at any time, effective at the end of the current billing period, and
        retain access until then; (d) we will give reasonable advance notice of price changes,
        which apply from your next renewal; and (e) except where required by law, fees are
        non-refundable, though we may issue refunds at our discretion. Taxes may be added where
        applicable.</p>
      </LegalSection>

      <LegalSection heading="7. Alerts and communications">
        <p>Filing alerts and email notifications are provided on a best-effort basis. We do not
        guarantee that any alert will be delivered, or delivered within any particular time after a
        filing is published, and you should not rely on alerts as your sole source of material
        information about any issuer.</p>
      </LegalSection>

      <LegalSection heading="8. Acceptable use">
        <p>You agree not to: (a) scrape, crawl, bulk-download, or systematically extract data from
        the Service, whether manually or by automated means, without our prior written consent;
        (b) circumvent access controls, paywalls, or usage limits; (c) resell, redistribute,
        sublicense, or offer the Service's data or derived datasets as part of a competing product
        or to train machine-learning models; (d) probe, disrupt, or interfere with the operation or
        security of the Service; (e) misrepresent your affiliation or impersonate others; or
        (f) use the Service in violation of applicable law, including securities laws.</p>
      </LegalSection>

      <LegalSection heading="9. Intellectual property">
        <p>The Service's software, design, compilation, structuring, verification methodology,
        and presentation of data are our intellectual property, protected by copyright, database,
        and other laws. Underlying SEC filings are public records. Subject to your compliance with
        these Terms, we grant you a limited, revocable, non-exclusive, non-transferable,
        non-sublicensable license to access the Service and use its content solely for your own
        internal, non-commercial research purposes. Except as expressly permitted by these Terms or
        by applicable law (including fair use), you may not copy, reproduce, distribute, publicly
        display, republish, or create derivative works from the Service or its content. Limited
        quotation for purposes of commentary, criticism, or news reporting is permitted with
        attribution to "Concentration of Risk" and, where practicable, a link to the Service; any
        systematic reproduction, redistribution, or republication of data requires our prior
        written consent. All rights not expressly granted are reserved.</p>
      </LegalSection>

      <LegalSection heading="10. Feedback">
        <p>If you send us ideas, suggestions, corrections, or other feedback, you grant us a
        perpetual, irrevocable, worldwide, royalty-free license to use it for any purpose without
        compensation or attribution, and you represent you have the right to grant that
        license.</p>
      </LegalSection>

      <LegalSection heading="11. Third-party services and links">
        <p>The Service links to third-party websites and services (including SEC EDGAR) and relies
        on third-party providers (including Google sign-in and Stripe). We do not control and are
        not responsible for the content, accuracy, availability, or practices of third parties.
        Your use of third-party services is governed by their own terms.</p>
      </LegalSection>

      <LegalSection heading="12. Copyright complaints">
        <p>If you believe content on the Service infringes your copyright, send a notice via the{" "}
        <Link to="/contact" className="text-[#1a1a18] underline underline-offset-2">contact page</Link>{" "}
        identifying the work, the allegedly infringing material and its location, your contact
        details, and a good-faith statement of unauthorized use. We will review and respond
        promptly.</p>
      </LegalSection>

      <LegalSection heading="13. Availability; no SLA; force majeure">
        <p>The Service is provided without any service-level commitment. We do not guarantee that
        the Service will be available, uninterrupted, or error-free, and we may suspend the Service
        for maintenance, updates, or security at any time without notice. We are not liable for any
        failure or delay caused by events beyond our reasonable control, including internet or
        hosting failures, third-party outages (including SEC EDGAR), labor disputes, governmental
        action, war, terrorism, epidemics, or natural disasters.</p>
      </LegalSection>

      <LegalSection heading="14. Disclaimer of warranties">
        <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
        EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, ACCURACY, COMPLETENESS, TIMELINESS, UNINTERRUPTED OR ERROR-FREE OPERATION, AND
        NON-INFRINGEMENT. WE DO NOT WARRANT THAT DEFECTS WILL BE CORRECTED OR THAT THE SERVICE IS
        FREE OF ERRORS OR HARMFUL COMPONENTS.</p>
      </LegalSection>

      <LegalSection heading="15. Limitation of liability">
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR ANY INDIRECT,
        INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE,
        DATA, OR INVESTMENT OR TRADING LOSSES, ARISING FROM OR RELATED TO YOUR USE OF (OR INABILITY
        TO USE) THE SERVICE OR ANY DATA ON IT — INCLUDING LOSSES ARISING FROM ANY ERROR, OMISSION,
        DELAY, OR INACCURACY IN THE DATA — EVEN IF ADVISED OF THE POSSIBILITY. OUR TOTAL AGGREGATE
        LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE WILL NOT EXCEED ONE HUNDRED U.S. DOLLARS
        (US $100) OR THE AMOUNT YOU PAID US IN THE TWELVE MONTHS PRECEDING THE CLAIM, WHICHEVER IS
        GREATER. Some jurisdictions do not allow certain limitations, so parts of this section may
        not apply to you; in that case our liability is limited to the greatest extent permitted.</p>
      </LegalSection>

      <LegalSection heading="16. Indemnification">
        <p>You agree to indemnify and hold us harmless from claims, damages, and reasonable
        expenses (including attorneys' fees) arising from your violation of these Terms or your
        misuse of the Service or its data.</p>
      </LegalSection>

      <LegalSection heading="17. Binding arbitration and class-action waiver">
        <p><strong className="text-neutral-800">Informal resolution first.</strong> Before filing
        any claim, you agree to contact us via the{" "}
        <Link to="/contact" className="text-[#1a1a18] underline underline-offset-2">contact page</Link>{" "}
        with a written description of the dispute and give us 60 days to resolve it informally.</p>
        <p><strong className="text-neutral-800">Arbitration.</strong> Any dispute arising out of or
        relating to these Terms or the Service that is not resolved informally will be resolved by
        binding arbitration administered by the American Arbitration Association under its Consumer
        Arbitration Rules, conducted in English by a single arbitrator, remotely or in the county
        where you reside. The Federal Arbitration Act governs this section. Judgment on the award
        may be entered in any court of competent jurisdiction. Either party may instead bring an
        individual claim in small-claims court, and either party may seek injunctive relief in
        court for infringement or misuse of intellectual property or unauthorized scraping of the
        Service.</p>
        <p><strong className="text-neutral-800">Class-action and jury waiver.</strong> ALL CLAIMS
        MUST BE BROUGHT IN THE PARTIES' INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER
        IN ANY PURPORTED CLASS, COLLECTIVE, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. THE
        ARBITRATOR MAY NOT CONSOLIDATE CLAIMS OR PRESIDE OVER ANY FORM OF REPRESENTATIVE
        PROCEEDING. YOU AND WE EACH WAIVE ANY RIGHT TO A JURY TRIAL. If the class-action waiver is
        found unenforceable as to a particular claim, that claim (and only that claim) shall
        proceed in court under Section 20, with the remainder in arbitration.</p>
        <p><strong className="text-neutral-800">Opt-out.</strong> You may opt out of this
        arbitration provision by notifying us via the contact page within 30 days of first
        accepting these Terms, stating your name, the email on your account, and your intent to opt
        out. Opting out does not affect any other provision of these Terms.</p>
      </LegalSection>

      <LegalSection heading="18. Termination">
        <p>You may stop using the Service and delete your account at any time. We may suspend or
        terminate access (including free accounts) for violation of these Terms or to protect the
        Service, and may discontinue features with or without notice. Sections that by their nature
        should survive termination (including 3, 4, 9, 10, and 14–17) survive.</p>
      </LegalSection>

      <LegalSection heading="19. Changes to the Terms">
        <p>We may revise these Terms from time to time. The "last updated" date above reflects the
        current version; material changes will, where appropriate, be announced on the Service or
        by email. Continued use of the Service after changes take effect constitutes acceptance of
        the revised Terms.</p>
      </LegalSection>

      <LegalSection heading="20. Governing law and venue">
        <p>These Terms are governed by the laws of the State of Delaware, USA, without regard to
        its conflict-of-laws rules. Subject to Section 17, any dispute not subject to arbitration
        will be resolved exclusively in the state or federal courts located in Delaware, and you
        consent to their jurisdiction and venue. Nothing in this section deprives you of mandatory
        consumer protections of the country or state where you reside.</p>
      </LegalSection>

      <LegalSection heading="21. Export and sanctions compliance">
        <p>You may not use the Service if you are located in, or are a resident or national of, any
        jurisdiction subject to comprehensive U.S. sanctions, or if you are on any U.S. government
        restricted-party list. You agree to comply with all applicable export control and sanctions
        laws.</p>
      </LegalSection>

      <LegalSection heading="22. Notices">
        <p>We may provide notices to you by email to the address associated with your account or by
        posting on the Service; notices are effective when sent or posted. Legal notices to us must
        be sent via the{" "}
        <Link to="/contact" className="text-[#1a1a18] underline underline-offset-2">contact page</Link>.</p>
      </LegalSection>

      <LegalSection heading="23. General">
        <p>These Terms and the Privacy Policy are the entire agreement between you and us regarding
        the Service. If any provision is held unenforceable, the remainder stays in effect. Our
        failure to enforce a provision is not a waiver. You may not assign these Terms; we may
        assign them in connection with a merger, acquisition, or sale of assets.</p>
      </LegalSection>

      <LegalSection heading="24. Contact">
        <p>Questions about these Terms? Reach us through the{" "}
        <Link to="/contact" className="text-[#1a1a18] underline underline-offset-2">contact page</Link>.</p>
      </LegalSection>
    </PageShell>
  );
}
